<script setup lang="ts">
import ShipmentQueueList from '@/components/shipment/list/ShipmentQueueList.vue';
import MetricsBar from '@/components/shipment/list/MetricsBar.vue';
import FilterBar from '@/components/shipment/list/FilterBar.vue';
import ShipmentsListBody from '@/components/shipment/list/ShipmentsListBody.vue';
import ShipmentsBulkActionsBar from '@/components/shipment/list/ShipmentsBulkActionsBar.vue';
import type {
  DataTablePageEvent,
  DataTableSortEvent,
} from 'primevue/datatable';

import type { ShipmentMetrics, ShipmentQueueKey } from '@/types/shipments';
import { SHIPMENT_DISPLAY_STATUS, SHIPMENT_QUEUE_FILTER } from '@/utils/shipment-status-ui';
import type { ShipmentListRow } from '@/types/shipments';
import type { V2ShipmentDetail } from '@gpx-spa/queries/src/getAccountShipmentV2';
import ShipmentListMap from '@/components/shipment/operational/ShipmentListMap.vue';

import {
  ACCOUNT_SHIPMENT_V2_INCLUDE,
  getAccountShipments,
  getAccountShipmentMetrics,
} from '@gpx-spa/queries';
import * as events from '@/events';
import { computed, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { afterNextBrowserPaint } from '@/utils/after-next-browser-paint';
import { useStore } from 'vuex';
import { setTitle } from '@/utils/title';
import { localDateToUtcIso } from '@/utils/utc-datetime';
import { endOfDay, startOfDay } from 'date-fns';
import { useShipmentsViewModeStore } from '@/stores/useShipmentsViewModeStore';
import { useMapBasemapModeStore } from '@/stores/useMapBasemapModeStore';
import { SHIPMENTS_MAP_LIST_PER_PAGE } from '@/config/constants';
import { buildShipmentListQueryParams } from '@/utils/shipment-list-query-params';
import { useShipmentListActions } from '@/composables/useShipmentListActions';

const store = useStore();
const router = useRouter();
const viewModeStore = useShipmentsViewModeStore();
const mapBasemapStore = useMapBasemapModeStore();
const viewModeReady = ref(false);

function goToExceptions() {
  void router.push({ name: 'ExceptionsListView' });
}

void viewModeStore.hydrateFromBrowserDb().finally(() => {
  viewModeReady.value = true;
});

setTitle('Shipments');
onMounted(() => {
  events.trigger(events.names.SHIPMENT_LIST_VIEWED);
  void mapBasemapStore.hydrateFromBrowserDb();
});

const accountId = computed(() => store.getters['auth/accountId'] as number);

const currentPage = ref(1);
const pageSize = ref(10);
const sortField = ref<string | null>(null);
const sortOrder = ref(1);
const activeQueue = ref<ShipmentQueueKey>('all');
const activeMetricFilter = ref<string | null>('active_shipments');

const search = ref('');
const carrier = ref<string[]>([]);
const pickupFromDate = ref<Date | null>(null);
const pickupToDate = ref<Date | null>(null);
const selectedShipments = ref<ShipmentListRow[]>([]);

const { canManage, confirmClose, confirmDelete } = useShipmentListActions({
  onSelectionCleared: () => {
    selectedShipments.value = [];
  },
});

function localStartOfDayUtcIso(d: Date): string {
  return localDateToUtcIso(startOfDay(d));
}

function localEndOfDayUtcIso(d: Date): string {
  return localDateToUtcIso(endOfDay(d));
}

const listViewMode = computed({
  get: () => viewModeStore.viewMode,
  set: (mode: 'table' | 'map') => viewModeStore.setViewMode(mode),
});

const apiParams = computed(() => {
  const mapMode = listViewMode.value === 'map';
  return buildShipmentListQueryParams({
    page: mapMode ? 1 : currentPage.value,
    perPage: mapMode ? SHIPMENTS_MAP_LIST_PER_PAGE : pageSize.value,
    include: ACCOUNT_SHIPMENT_V2_INCLUDE,
    sortField: sortField.value,
    sortOrder: sortOrder.value,
    activeQueue: activeQueue.value,
    activeMetricFilter: activeMetricFilter.value,
    search: search.value,
    carrier: carrier.value,
    pickupFromUtcIso: localStartOfDayUtcIso,
    pickupToUtcIso: localEndOfDayUtcIso,
    pickupFromDate: pickupFromDate.value,
    pickupToDate: pickupToDate.value,
  });
});

const { data: metricsData, isFetched: metricsFetched } =
  getAccountShipmentMetrics(accountId);
const {
  data: shipmentsResponse,
  isLoading,
  isPlaceholderData,
} = getAccountShipments(accountId, apiParams);

const listMapLoading = computed(
  () => isLoading.value || isPlaceholderData.value,
);

const shipments = computed(
  () => (shipmentsResponse.value?.data ?? []) as V2ShipmentDetail[],
);

const totalRecords = computed(() => shipmentsResponse.value?.meta?.total ?? 0);

const metrics = computed<ShipmentMetrics>(() => {
  if (!metricsData.value) {
    return {
      total: 0,
      [SHIPMENT_DISPLAY_STATUS.Booked]: 0,
      [SHIPMENT_DISPLAY_STATUS.Active]: 0,
      [SHIPMENT_QUEUE_FILTER.Late]: 0,
      [SHIPMENT_DISPLAY_STATUS.Delivered]: 0,
      draft: 0,
      [SHIPMENT_DISPLAY_STATUS.Closed]: 0,
      picked_up_today: 0,
      delivery_today: 0,
      carriers: [],
    };
  }
  return metricsData.value as ShipmentMetrics;
});

const carrierOptions = computed<string[]>(() => metrics.value.carriers ?? []);

const showBulkCloseAction = computed(
  () => activeQueue.value !== SHIPMENT_DISPLAY_STATUS.Closed,
);

const selectedIds = computed(() =>
  selectedShipments.value.map((s) => s.id),
);

function clearSelection() {
  selectedShipments.value = [];
}

watch(
  [
    activeQueue,
    activeMetricFilter,
    search,
    carrier,
    pickupFromDate,
    pickupToDate,
    listViewMode,
  ],
  () => {
    currentPage.value = 1;
    clearSelection();
  },
);

watch([currentPage, pageSize], () => {
  clearSelection();
});

const operationalMapMountReady = ref(false);

watch(
  listViewMode,
  (mode) => {
    if (mode === 'map') {
      currentPage.value = 1;
      operationalMapMountReady.value = false;
      clearSelection();
      void afterNextBrowserPaint().then(() => {
        if (listViewMode.value === 'map') {
          operationalMapMountReady.value = true;
        }
      });
    } else {
      operationalMapMountReady.value = false;
    }
  },
  { immediate: true },
);

function handleQueueSelect(queue: ShipmentQueueKey) {
  activeQueue.value = queue;
  activeMetricFilter.value = null;
}

function handleMetricClick(key: string) {
  const next = activeMetricFilter.value === key ? null : key;
  activeMetricFilter.value = next;
  if (next != null) {
    activeQueue.value = 'all';
  }
}

function onPage(event: DataTablePageEvent) {
  currentPage.value = event.page + 1;
  pageSize.value = event.rows;
}

function onSort(event: DataTableSortEvent) {
  sortField.value = event.sortField as string | null;
  sortOrder.value = event.sortOrder ?? 1;
  currentPage.value = 1;
}

/** All Shipments queue count — every shipment visible to the user in this account. */
const accountHasNoShipments = computed(
  () => metricsFetched.value && metrics.value.total === 0,
);

function clearFilters() {
  activeQueue.value = 'all';
  activeMetricFilter.value = null;
  search.value = '';
  carrier.value = [];
  pickupFromDate.value = null;
  pickupToDate.value = null;
}

function handleBulkClose() {
  confirmClose(selectedIds.value);
}

function handleBulkDelete() {
  confirmDelete(selectedIds.value);
}
</script>

<template>
  <div class="h-layout-mobile sm:h-layout-tablet md:h-layout-desktop flex">
    <div
      class="flex w-56 shrink-0 flex-col border-r border-gray-200 bg-white"
    >
      <div class="flex gap-1 border-b border-gray-100 px-3 py-2">
        <button
          type="button"
          class="flex-1 rounded-md bg-gray-100 px-2 py-1.5 text-sm font-medium text-gray-900"
        >
          Shipments
        </button>
        <button
          type="button"
          class="flex-1 rounded-md px-2 py-1.5 text-sm font-medium text-gray-500 hover:bg-gray-50"
          @click="goToExceptions"
        >
          Exceptions
        </button>
      </div>
      <ShipmentQueueList
        :metrics="metrics"
        :active-queue="activeQueue"
        :active-metric-filter="activeMetricFilter"
        @select="handleQueueSelect"
        @metric-click="handleMetricClick"
      />
    </div>

    <div
      class="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-gpx_gray-900"
    >
      <div class="shrink-0 px-6 pb-4 pt-5">
        <div class="mb-3 flex gap-1 md:hidden">
          <button
            type="button"
            class="rounded-md bg-gray-100 px-3 py-1.5 text-sm font-medium"
          >
            Shipments
          </button>
          <button
            type="button"
            class="rounded-md px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
            @click="goToExceptions"
          >
            Exceptions
          </button>
        </div>
        <MetricsBar
          :metrics="metrics"
          :active-metric-filter="activeMetricFilter"
          @card-click="handleMetricClick"
        />
      </div>

      <div class="flex min-h-0 flex-1 flex-col px-6 pb-4">
        <div
          v-if="!viewModeReady"
          class="min-h-0 flex-1 rounded-lg bg-gray-100"
          role="status"
          aria-label="Loading shipments view"
        />
        <template v-else>
        <div class="space-y-3 pb-4">
          <ShipmentsBulkActionsBar
            v-if="listViewMode === 'table' && selectedShipments.length > 0"
            :selected-count="selectedShipments.length"
            :show-close-action="showBulkCloseAction"
            @close="handleBulkClose"
            @delete="handleBulkDelete"
            @clear="clearSelection"
          />
          <FilterBar
            :search="search"
            :carrier="carrier"
            :carrier-options="carrierOptions"
            :pickup-from-date="pickupFromDate"
            :pickup-to-date="pickupToDate"
            :total-count="totalRecords"
            :view-mode="listViewMode"
            :can-manage="canManage"
            @update:search="search = $event"
            @update:carrier="carrier = $event"
            @update:pickup-from-date="pickupFromDate = $event"
            @update:pickup-to-date="pickupToDate = $event"
            @update:view-mode="listViewMode = $event"
            @clear="clearFilters"
          />
        </div>

        <div
          v-if="listViewMode === 'table'"
          class="min-h-0 flex-1 overflow-auto"
        >
          <ShipmentsListBody
            :is-loading="isLoading"
            :shipments="shipments"
            :total-records="totalRecords"
            :account-has-no-shipments="accountHasNoShipments"
            :current-page="currentPage"
            :page-size="pageSize"
            :sort-field="sortField"
            :sort-order="sortOrder"
            :selected-shipments="selectedShipments"
            :can-manage="canManage"
            @update:selected-shipments="selectedShipments = $event"
            @page="onPage"
            @sort="onSort"
            @clear-filters="clearFilters"
          />
        </div>

        <div
          v-else
          class="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white"
        >
          <div
            v-if="!operationalMapMountReady"
            class="min-h-0 flex-1 bg-gray-100"
            role="status"
            aria-label="Loading map"
          />
          <ShipmentListMap
            v-else
            :account-id="accountId"
            :shipments="shipments"
            :loading="listMapLoading"
          />
        </div>
        </template>
      </div>
    </div>
  </div>
</template>
